import React from 'react';
import { Link } from 'react-router-dom';
import { formatCents, formatPct } from '../../utils/money';
import { fmtStamp } from '../proposalGrid/helpers';

// PRD-031 Cluster E — the Agreement header, rebuilt to the ratified
// economics-row-final.html: an Ink pagehead band (the ONE ink surface on the
// page, E2) carrying breadcrumb + title + status pill, then three white hero
// cards on Paper. Header money renders whole dollars and header percentages
// one decimal (F3.1); the grid below keeps Fidelity-grade cents.
//
// The breadcrumb names the COUNTERPARTY (the Agency sees the Client, the
// Client sees the Agency) — never one fixed side.

const RAIL_STEPS = ['drafting', 'signed', 'verifying', 'settled'];

export function StatusRail({ active }) {
  const activeIdx = RAIL_STEPS.indexOf(active);
  return (
    <div className="flex items-center gap-2" aria-label={`Status: ${active}`}>
      {RAIL_STEPS.map((step, i) => {
        const isActive = i === activeIdx;
        const isPast = i < activeIdx;
        return (
          <React.Fragment key={step}>
            {i > 0 && <span className={`h-px w-6 ${isPast || isActive ? 'bg-primary-400' : 'bg-gray-300'}`} />}
            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
              isActive ? 'bg-primary-600 text-white'
                : isPast ? 'bg-primary-50 text-primary-600'
                  : 'border border-gray-300 text-gray-600'}`}>
              {step}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function HeroCard({ k, v, pctnote, sub, small }) {
  return (
    <div className="ws-hcard">
      <div className="k">{k}</div>
      <div className="v" style={small ? { fontSize: '19px' } : undefined}>
        {v}{pctnote != null && <span className="pctnote"> {pctnote}</span>}
      </div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

function MetaItem({ k, v, who }) {
  if (!v) return null;
  return (
    <div className="flex flex-col">
      <span className="text-[9.5px] tracking-wide uppercase text-gray-600 font-extrabold">{k}</span>
      <span className="text-[12.5px] text-gray-700 font-semibold tabular-nums mt-px">
        {v}{who && <span className="text-gray-600 font-medium"> {who}</span>}
      </span>
    </div>
  );
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null);
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null);

export default function PnlHeaderCard({
  parties, title, railStep, totals, timeline, delivery, deliveryHref,
  costVisible, currency, isSeller, shared, onToggleShare,
}) {
  const whole = { currency, decimals: 0 };
  const rev = totals.revenue_cents || 0;
  const cost = totals.cost_cents || 0;
  const est = totals.estimated_cost_cents || 0;
  const counterparty = isSeller ? parties.buyer?.name : parties.seller?.name;
  const statusLabel = railStep === 'settled' ? 'Settled' : 'Signed · in delivery';

  // Header math is a projection of the canonical server totals: realized
  // margin = Agreement value − actual cost to date; projected-at-signing % =
  // (value − frozen estimate) / value.
  const realizedMargin = rev - cost;
  const realizedPct = rev > 0 ? (realizedMargin / rev) * 100 : 0;
  const projectedPct = rev > 0 ? ((rev - est) / rev) * 100 : 0;

  const n = delivery?.outcomeCount || 0;
  const asOf = delivery?.lastCheckedAt ? `as of ${fmtStamp(delivery.lastCheckedAt)}` : null;

  return (
    <div>
      {/* E2 — the one Ink band on the page. */}
      <div className="ws-pagehead">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="crumb"><Link to="/agreements">Agreements</Link> › <b>{counterparty || '—'}</b></div>
            <h1>{title || 'Agreement'} <span className="statuspill">{statusLabel}</span></h1>
          </div>
          {/* WU-F (RJ, staging pass 2026-08-09): every verification action
              lives on the Delivery page, and the only route to it was a
              12.5px link at breadcrumb weight in the corner of this band.
              It read as navigation chrome. A real button, going straight to
              the Evidence tab — the thing the agency came here to do. */}
          {deliveryHref && (
            <Link
              // The agency lands on Evidence, which is what they came to
              // do; the client lands on the default status tab, where their
              // actions are. One button, two honest destinations.
              to={isSeller ? `${deliveryHref}?tab=evidence` : deliveryHref}
              data-testid="delivery-cta"
              className="shrink-0 mt-0.5 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-[13.5px] font-bold text-white bg-primary-600 hover:bg-primary-700"
            >
              Go to Delivery <span aria-hidden="true">›</span>
            </Link>
          )}
        </div>
      </div>

      {/* Three hero cards — whole-dollar money, one-decimal percentages. */}
      <div className="ws-hero">
        <HeroCard k="Agreement value" v={formatCents(rev, whole)} sub={`${n} outcome${n === 1 ? '' : 's'}`} />
        {costVisible ? (
          <>
            <HeroCard k="Actual cost" v={formatCents(cost, whole)}
              sub={`vs ${formatCents(est, whole)} estimated${asOf ? ` · ${asOf}` : ''}`} />
            <HeroCard k="Realized margin" v={formatCents(realizedMargin, whole)} pctnote={`· ${formatPct(realizedPct, 1)}`}
              sub={`vs ${formatPct(projectedPct, 1)} projected at signing`} />
          </>
        ) : (
          <>
            <HeroCard k="Outcomes delivered" v={String(delivery?.deliveredCount || 0)} pctnote={`of ${n}`}
              sub={delivery?.pendingAcceptanceCount > 0
                ? `${delivery.pendingAcceptanceCount} awaiting your acceptance` : null} />
            <HeroCard k="Last checked" small
              v={delivery?.lastCheckedAt ? fmtStamp(delivery.lastCheckedAt) : '—'}
              sub={delivery?.allCheckedWithin24h ? 'every outcome checked in the last 24 hours' : null} />
          </>
        )}
      </div>

      {/* Sharing control + lifecycle rail + signed-facts strip. */}
      <div className="card px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        <StatusRail active={railStep} />
        {isSeller && (
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer shrink-0 ml-auto">
            <input type="checkbox" checked={!!shared} onChange={(e) => onToggleShare(e.target.checked)} />
            Share P&amp;L with the client
          </label>
        )}
      </div>

      {timeline && (
        <div className="card px-5 py-3 mt-3 flex flex-wrap gap-x-6 gap-y-1.5">
          <MetaItem k="Created" v={fmtDate(timeline.created_at)} />
          <MetaItem k="Signed — agency" v={fmtDateTime(timeline.seller_signed_at)} />
          <MetaItem k="Signed — client" v={fmtDateTime(timeline.buyer_signed_at)} />
          <MetaItem k="Effective" v={timeline.term_starts_at ? `${fmtDate(timeline.term_starts_at)}${timeline.term_ends_at ? ` → ${fmtDate(timeline.term_ends_at)}` : ''}` : null} />
          <MetaItem k="Due date" v={fmtDate(timeline.due_date)} />
        </div>
      )}

      {/* PRD-031 E2a — the Verdict fee is the Agency's cost of using the
          platform; it never appears in either Client view. costVisible is
          false only for the Client, so the old fee-disclosure note is gone
          rather than reworded. */}
    </div>
  );
}
